/**
 * Get file meta
 * @param {any} file
 * @param {boolean} isImage
 */
export const getFileMeta = async (file: any, isImage: boolean) => {
	let fileMeta = {
		fileUrl: file.fileUrl,
		name: file.name,
		width: 800,
		height: 600,
	};

	const isAudioFile = isAudio(file.fileUrl!);

	if (isAudioFile) {
		fileMeta.width = 200;
		fileMeta.height = 200;
		return fileMeta;
	}

	if (isVideo(file.fileUrl)) {
		try {
			const { height, width } = await getVideoMetadata(file.fileUrl || file.url);
			fileMeta.width = width;
			fileMeta.height = height;
		} catch {}
		return fileMeta;
	}
	if (isImage) {
		const { width, height } = await getImageSize(file.fileUrl);

		fileMeta.width = width;
		fileMeta.height = height;
	}

	return fileMeta;
};

const videoMetadataCache: Record<string, { width: number; height: number; duration: number }> = {};

async function getVideoMetadata(
	videoUrl: string,
): Promise<{
	width: number;
	height: number;
	duration: number;
}> {
	// Check if the metadata is already cached for the video URL
	if (videoMetadataCache[videoUrl]) {
		return Promise.resolve(videoMetadataCache[videoUrl]);
	}

	return new Promise((resolve, reject) => {
		const video = document.createElement("video");
		video.preload = "metadata";
		video.muted = true; // Mute the video
		video.playsInline = true; // Prevents the video from taking over full screen on mobile
		video.autoplay = false; // Ensure autoplay is disabled

		// Attach event listener for when metadata is loaded
		video.onloadedmetadata = () => {
			// Pause the video to ensure it doesn't play
			video.pause();

			const metadata = {
				width: video.videoWidth,
				height: video.videoHeight,
				duration: video.duration,
			};

			// Cache the metadata for future use
			videoMetadataCache[videoUrl] = metadata;

			resolve(metadata);
		};

		// Handle error loading the video
		video.onerror = () => {
			reject(new Error("Failed to load video metadata"));
		};

		// Set the video source and load the video metadata
		video.src = videoUrl;
		video.load();
	});
}

export const isAudio = (fileUrl: string) => [".wav", ".mp3", ".ogg"].some((ext) => fileUrl.toLowerCase().includes(ext));

export const isPdfFile = (fileUrl: string) => fileUrl.toLowerCase().includes(".pdf");

export const isVideo = (fileUrl: string) =>
	[".mp4", ".mov", ".wmv", ".avi", ".webm"].some((ext) => fileUrl.toLowerCase().includes(ext));

export function getImageSize(source: string): Promise<{ width: number; height: number }> {
	return new Promise((resolve, reject) => {
		let img = new Image();
		img.onload = () => {
			resolve({ width: img.width, height: img.height });
		};
		img.onerror = () => {
			reject(new Error("Unable to load image"));
		};
		img.src = source;
	});
}

const MAX_FILENAME_LENGTH = 16;
const END_FILENAME_LENGTH = 3;
const DOTS_LENGTH = 3;
const FIRST_LETTERS = MAX_FILENAME_LENGTH - END_FILENAME_LENGTH;

/**
 * Given filename, this function will try to shorten given filename.
 * Example: INBOX_startchallengeAttachement.png -> INBOX_startchalle...ent.png
 */
export function shortenFilename(filename: string) {
	const [name, extension] = filename.split(".");
	if (name.length < MAX_FILENAME_LENGTH) {
		return filename;
	}

	const shorten = name.substr(0, FIRST_LETTERS) + "..." + name.substr(-DOTS_LENGTH);

	if (extension) {
		return shorten + "." + extension;
	}

	return shorten;
}
